import { useCallback, useEffect, useState, useRef, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Icon, Typography } from 'shared/ui'
import { hasMergeFieldToken, isLink, logger, stripMergeFieldTokens } from 'shared/lib'
import { makeTextFieldAutoFocus, TextField } from 'shared/ui/TextField'
import { type ShortLinkStore } from 'entities/ShortLink/store/shortLinkStore'
import styles from './styles.module.scss'

type IShortLinkContentProps = {
  store: ShortLinkStore
}

export const ShortLinkContent: FC<IShortLinkContentProps> = observer(({ store }) => {
  const [plainLink, setPlainLink] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [failed, setFailed] = useState(false)

  const { editLink, createPersonalizedShortLink, handleShortenLink, handleAddShortLink } = store
  const { handleSaveLink, urlEditor } = store

  const inputRef = useRef<HTMLInputElement>(null)
  const focusPositionRef = useRef<number | null>(null)
  const link = urlEditor ? urlEditor.text : plainLink
  const validatableLink = stripMergeFieldTokens(link)
  const isAddDisabled = !link.trim() || loading || !store.canShortUrl(validatableLink)

  const onChangeValue = (value: string) => {
    setError(false)
    setFailed(false)
    setPlainLink(value)
  }

  const handleInsertMergeFieldValue = useCallback((value: string) => {
    const inserted = hasMergeFieldToken(value) ? value : encodeURIComponent(value)
    const position = inputRef.current?.selectionStart ?? 0

    setError(false)
    setPlainLink((previousLink) => {
      const pos = Math.min(position, previousLink.length)
      focusPositionRef.current = pos + inserted.length

      return previousLink.slice(0, pos) + inserted + previousLink.slice(pos)
    })
  }, [])

  useEffect(() => {
    if (urlEditor || focusPositionRef.current === null) return

    const input = inputRef.current
    const cursor = focusPositionRef.current
    focusPositionRef.current = null

    if (input) {
      input.focus()
      input.setSelectionRange(cursor, cursor)
    }
  }, [plainLink, urlEditor])

  useEffect(() => {
    if (!editLink) return

    if (urlEditor) {
      urlEditor.setText(editLink.innerText)
      return
    }
    setPlainLink(editLink.href)

    if (inputRef.current) {
      const end = editLink.href.length
      inputRef.current.setSelectionRange(end, end)
      inputRef.current.focus()
    }
  }, [editLink, urlEditor])

  const onAdd = async () => {
    if (!isLink(validatableLink)) {
      setError(true)
      return
    }
    setError(false)
    setFailed(false)
    setLoading(true)
    try {
      const shortUrl = await createPersonalizedShortLink(link)

      if (!shortUrl) {
        setFailed(true)
        return
      }
      if (editLink) {
        handleShortenLink(shortUrl)
      } else {
        handleAddShortLink(shortUrl)
      }
    } catch (e) {
      logger.error(e)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  const onSave = () => {
    if (!isLink(validatableLink)) {
      setError(true)
      return
    }
    setError(false)
    handleSaveLink(link)
  }

  return (
    <div className={styles.wrap}>
      {urlEditor ? (
        urlEditor.renderField()
      ) : (
        <TextField
          className={styles.textField}
          variant='integrated'
          size='small'
          InputProps={{
            placeholder: 'Enter your URL',
            ref: inputRef,
          }}
          onChange={onChangeValue}
          value={plainLink}
          ariaLabel='LabelShortenUrl'
          mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
        />
      )}
      {(error || failed) && (
        <div className={styles.helper}>
          <Icon
            icon={'alertTriangleSmall'}
            color={'var(--content-negative-primary)'}
            fontSize={16}
          />
          <Typography
            variant={'text-input-helper'}
            color={'negativePrimary'}
            ariaLabel={'Please enter'}
          >
            {failed ? 'Could not shorten this URL. Please try again.' : 'Please enter a valid URL'}
          </Typography>
        </div>
      )}
      <div className={styles.actions}>
        {urlEditor
          ? urlEditor.renderMergeFieldAction()
          : store.mergeFieldAction?.(handleInsertMergeFieldValue)}
        <div className={styles.actionsRight}>
          {editLink ? (
            <>
              <Button
                text={'Shorten URL'}
                size={'medium'}
                contained={'secondary'}
                disabled={isAddDisabled}
                onClick={onAdd}
              />
              <Button
                text={'Save'}
                size={'medium'}
                contained={'primary'}
                disabled={!link.trim()}
                onClick={onSave}
              />
            </>
          ) : (
            <Button
              text={'Add URL'}
              size={'medium'}
              contained={'secondary'}
              disabled={isAddDisabled}
              onClick={onAdd}
            />
          )}
        </div>
      </div>
    </div>
  )
})
